<template>
	<v-container class="dashboard-container">
		<v-row class="contentRow" justify="center">
			<v-col cols="8">
				<div class="steps-wrapper">
					<!-- Step 1 -->
					<div class="step-container clickable-step">
						<div class="step-circle">
							<v-icon size="35" color="trace">fas fa-book-open</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Handbooks</b></h4>
						</div>
					</div>

					<!-- Arrow 1 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 2 -->
					<div class="step-container clickable-step">
						<div class="step-circle">
							<v-icon size="35" color="error">fas fa-share-from-square</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Procedure</b></h4>
						</div>
					</div>

					<!-- Arrow 2 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 3 -->
					<div class="step-container clickable-step">
						<div class="step-circle">
							<v-icon size="35" color="success">fas fa-chalkboard-user</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Instruction</b></h4>
						</div>
					</div>

					<!-- Arrow 3 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 4 -->
					<div class="step-container clickable-step">
						<div class="step-circle">
							<v-icon size="35" color="warning">fas fa-cog</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Control</b></h4>
						</div>
					</div>
				</div>
			</v-col>
		</v-row>

		<v-row justify="center">
			<v-col cols="12">
				<h3>Show how each area is put into practice in your organisation.</h3>
				<p>
					Identify the relevant handbook, procedure, instruction or control where the
					requirements are implemented in everyday operations.
				</p>

				<div class="table-wrapper">
					<table class="implementation-table">
						<thead>
							<tr>
								<th class="col-subject">Subject</th>
								<th>Where is this implemented?</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in implementationRows" :key="row.subject">
								<td class="col-subject">{{ row.subject }}</td>
								<td>
									<div class="impl-cell">
										<v-textarea
											v-model="row.value"
											class="impl-field"
											:placeholder="row.placeholder"
											:readonly="!row.editing"
											:outlined="row.editing"
											:solo="!row.editing"
											:flat="!row.editing"
											background-color="transparent"
											auto-grow
											rows="1"
											dense
											hide-details
										/>
										<v-btn
											icon
											small
											class="impl-edit-btn"
											:title="row.editing ? 'Save' : 'Edit'"
											@click="toggleEdit(row)"
										>
											<v-icon small>{{ row.editing ? "fas fa-floppy-disk" : "fas fa-pen" }}</v-icon>
										</v-btn>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import LoadingState from "../consts/LoadingState"
import { riskAPI } from "../api/csr/risk"

export default {
	data() {
		return {
			implementationRows: [
				{
					subject: "Anti-corruption",
					field: "Anticorruption",
					placeholder: "Ex:- Supplier Code of Conduct, Gifts & Hospitality Procedure, Approval Control",
					value: "",
					editing: false,
				},
				{
					subject: "Cybersecurity",
					field: "Cybersecurity",
					placeholder: "Ex:- Information Security Handbook, Access Management Procedure, MFA Control",
					value: "",
					editing: false,
				},
				{
					subject: "Labour conditions",
					field: "Labour",
					placeholder: "Ex:- HR Handbook, Working Conditions Procedure, Employee Review",
					value: "",
					editing: false,
				},
			],
		}
	},
	mounted() {
		this.viewProcedureData()
	},
	methods: {
		viewProcedureData() {
			riskAPI.viewProcedures()
				.then(result => {
					// API returns [ [rowsFromDb] ]; there is at most one row per client
					const saved = (result && Array.isArray(result[0]) && result[0][0]) || {}
					this.implementationRows.forEach(row => {
						row.value = saved[row.field] || ""
					})
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		toggleEdit(row) {
			if (!row.editing) {
				row.editing = true
				return
			}
			this.saveRow(row)
		},
		saveRow(row) {
			// The endpoint saves all three columns at once, so send the current value of each
			const v = field => this.implementationRows.find(r => r.field === field).value
			riskAPI.saveProcedure(v("Anticorruption"), v("Cybersecurity"), v("Labour"))
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully saved!`,
						{ root: true }
					)
					row.editing = false
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
	},
}
</script>

<style scoped>
.steps-wrapper {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	gap: 24px;
	position: relative;
	z-index: 2;
	padding: 32px 40px;
	border: 2px solid #1e7c83;
	border-radius: 5px;
}

.step-container {
	text-align: center;
	max-width: 100px;
}

.step-circle {
	width: 70px;
	height: 70px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 20px auto;
	backdrop-filter: blur(10px);
	transition: all 0.3s ease;
	box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.step-circle:hover {
	transform: translateY(-2px);
}

.arrow-col {
	flex: 0 0 auto;
	width: auto;
	max-width: none;
	height: 70px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0;
	padding: 0 8px;
}

/* ---- Implementation table ---- */
.table-wrapper {
	overflow-x: auto;
	margin-top: 16px;
}

.implementation-table {
	width: 100%;
	border-collapse: collapse;
	background-color: #f2f7f2;
}

.implementation-table th,
.implementation-table td {
	text-align: left;
	padding: 12px 20px;
	border: 2px solid #ffffff;
	font-size: 1rem;
	vertical-align: middle;
}

.implementation-table th {
	font-weight: 400;
}

.implementation-table .col-subject {
	width: 20%;
	white-space: nowrap;
}

.impl-cell {
	display: flex;
	align-items: center;
	gap: 8px;
}

.impl-field {
	flex: 1 1 0;
	min-width: 0;
	margin: 0;
	padding: 0;
}

.impl-edit-btn {
	flex: 0 0 auto;
}

@media (max-width: 768px) {
	.steps-wrapper {
		flex-direction: column;
		align-items: center;
		gap: 10px;
		padding: 24px;
	}

	.arrow-col {
		height: auto;
		padding: 10px 0;
	}

	.arrow-col .v-icon {
		transform: rotate(90deg);
	}

	.step-container {
		max-width: 200px;
	}

}

@media (max-width: 1024px) and (min-width: 769px) {
	.steps-wrapper {
		padding: 24px;
		gap: 12px;
	}

	.step-circle .v-icon {
		font-size: 30px !important;
	}
}

@media (min-width: 1025px) {
	.steps-wrapper {
		padding: 32px 48px;
	}
}
</style>